/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react';
import { ICommandBarItemProps, ICommandBarProps, ICommandBarStyles } from '@fluentui/react/lib/CommandBar';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { DocumentFilterDropdown } from 'src/customText/appShell/dataTaggingPage/components/DocumentFilterDropdown';
import { FileView, FilesViewDropdown } from 'src/customText/appShell/dataTaggingPage/components/FilesViewDropdown';
import { DocumentsFilter } from 'src/customText/appShell/dataTaggingPage/samplerStore';
import { CustomTextDocumentStore } from 'src/customText/core/stores/CustomTextDocumentStore';

type AllFilesViewCommandBarProps = TableCommandBarProps<CustomTextDocumentStore> & {
    isCommandBarDisabled?: boolean;
    isExtractionProject: boolean;
    appliedFilters: DocumentsFilter;
    onFileViewChange: (fileView: FileView) => void;
    onChangeFilter: (filter: DocumentsFilter) => void;
};

const messages = defineMessages({
    searchPlaceholder: {
        id: 'customText.allFilesViewCommandBar.searchPlaceholder',
        defaultMessage: 'Search'
    }
});

const commandBarStyles = classNamesFunction<ICommandBarProps, ICommandBarStyles>()({
    secondarySet: { alignItems: 'center' }
});

/**
 * Command bar for the documents list displayed in the all files view of the tagging page.
 */
export const AllFilesViewCommandBar = (props: AllFilesViewCommandBarProps) => {
    const {
        query = '',
        isCommandBarDisabled = false,
        onQueryChange,
        onFileViewChange,
        onChangeFilter,
        appliedFilters,
        isExtractionProject
    } = props;
    const { localize } = useLocalization();

    const commands: ICommandBarItemProps[] = [
        {
            key: 'viewControl',
            onRender: () => (
                <Stack role="menuitem">
                    <FilesViewDropdown isDisabled={isCommandBarDisabled} selectedViewingOption="all" onFileViewChange={onFileViewChange} />
                </Stack>
            )
        }
    ];

    const farItems: ICommandBarItemProps[] = [
        {
            key: 'search',
            onRender: () => (
                <LuisDebouncedSearchBox
                    value={query}
                    disabled={isCommandBarDisabled}
                    onChange={(_, newValue?: string) => onQueryChange(newValue)}
                    placeholder={localize(messages.searchPlaceholder)}
                    ariaLabel={localize(messages.searchPlaceholder)}
                />
            )
        },
        {
            key: 'filter',
            onRender: () => (
                <DocumentFilterDropdown
                    isDisabled={isCommandBarDisabled}
                    isExtractionProject={isExtractionProject}
                    appliedFilters={appliedFilters}
                    onChangeFilter={onChangeFilter}
                />
            )
        }
    ];

    return <LuisCommandBar query={query} items={commands} farItems={farItems} styles={commandBarStyles} onQueryChange={onQueryChange} />;
};
